import { base, baseSepolia, Chain } from 'wagmi/chains';
import { Environment, getCurrentEnvironment } from './environment';

// Parse comma-separated lists from environment variables
const BASE_RPC_URLS =
  process.env.NEXT_PUBLIC_BASE_RPC_URLS?.split(',').map((url) => url.trim()) ??
  base.rpcUrls.default.http;
const BASE_SEPOLIA_RPC_URLS =
  process.env.NEXT_PUBLIC_BASE_SEPOLIA_RPC_URLS?.split(',').map((url) => url.trim()) ??
  baseSepolia.rpcUrls.default.http;

// Custom RPC URLs configuration, using lists of endpoints for fallback
const RPC_URLS: Record<number, { default: string[]; fallback: string[] }> = {
  [base.id]: {
    default: BASE_RPC_URLS,
  },
  [baseSepolia.id]: {
    default: BASE_SEPOLIA_RPC_URLS,
  },
};

// Configure chains with custom RPC URLs using the new lists
const configureChain = (chain: Chain): Chain => ({
  ...chain,
  rpcUrls: {
    ...chain.rpcUrls,
    default: {
      http: RPC_URLS[chain.id].default,
    },
  },
});

// Configure supported chains for each environment
export const SUPPORTED_CHAINS: Record<Environment, [Chain, ...Chain[]]> = {
  [Environment.localhost]: [configureChain(baseSepolia), configureChain(base)],
  [Environment.development]: [configureChain(baseSepolia), configureChain(base)],
  // Keep Base Mainnet first in the list on prod so we default to it when disconnected.
  [Environment.production]: [configureChain(base), configureChain(baseSepolia)],
  [Environment.test]: [configureChain(baseSepolia)],
};

/**
 * Gets the list of supported chains for a given environment.
 * Defaults to the current environment.
 * @param env
 */
export function getChainsForEnvironment(env?: Environment) {
  if (!env) {
    env = getCurrentEnvironment();
  }
  return SUPPORTED_CHAINS[env];
}

// Returns the chain for a given chainId when it's supported by the current environment.
export function getChainById(chainId: string) {
  const chains = getChainsForEnvironment();
  return chains?.find((c: Chain) => c.id === Number(chainId)) ?? null;
}

// Map chain IDs to chains for easy lookup
export const CHAIN_ID_TO_CHAIN: Record<number, Chain> = {
  [baseSepolia.id]: baseSepolia,
  [base.id]: base,
};

// Returns the wagmi chain for a given chainId, even when it's not supported by the current environment.
export function getWagmiChainById(chainId?: number): Chain | null {
  if (!chainId) return null;
  return CHAIN_ID_TO_CHAIN[chainId] || null;
}

// Export configured chains for use in RainbowKit/Wagmi setup
export const configuredChains = getChainsForEnvironment();
